import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BaseEditModel } from "model/BaseEditModel";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableRowEditEvent, DataTableRowReorderEvent } from "primereact/datatable";
import { Ripple } from "primereact/ripple";
import { ReactNode, useState } from "react";
import { generateKey } from "utils/generateKey";

type Props = {
    children?: ReactNode;
    isSelection: boolean;
    tituloLista: string;
    selected?: BaseEditModel | null;
    dataKey: string;
    setSelected?(value: any): void;
    lista: BaseEditModel[] | [];
    setLista(value: BaseEditModel[]): void;
    getNewModel(): BaseEditModel;
    reordable: boolean;
    onRowReorder: (e: DataTableRowReorderEvent<[] | BaseEditModel[]>) => void;
    addButtonDisabled: boolean;
    scrollable: boolean;
    scrollHeight: string;
    showEdit: boolean;
    showDelete: boolean;
    defaultSequencial?: number;
    rowGroupMode?: string;
    groupRowsBy?: string;
    sortMode?: "multiple" | "single";
    rowGroupHeaderTemplate?: (data: any) => ReactNode;
    rowGroupFooterTemplate?: (data: any) => ReactNode;
    headerColumnGroup?: ReactNode;
    rowCancelDisabled(rowData: any): boolean;
    onDeleteRow(list: any[]): void;
    extraButtons?: ReactNode
}

const BaseDataTableEdit = (props: Props) => {
    const [originalRows, setOriginalRows] = useState<any>({});
    const [editingRows, setEditingRows] = useState<any>({});

    const header = (
        <div style={{ display: "flex", justifyContent: "space-between", textAlign: 'center', alignContent: "center" }}>
            <div></div>
            <span style={{ textAlign: 'center', alignSelf: "center", position: 'relative' }}>{props.tituloLista}</span>
            <div>
                {props.extraButtons ? props.extraButtons : null}
                <Button
                    type="button" icon={<FontAwesomeIcon icon="add" />}
                    tooltip="Adicionar"
                    disabled={props.addButtonDisabled}
                    tooltipOptions={{ position: "top" }} className="ui-button-info" onClick={() => novo()}
                ></Button>
            </div>
        </div>
    )


    const novo = () => {
        let novaClasse = props.getNewModel();
        novaClasse.id = null;
        novaClasse.key = generateKey();
        if (props.defaultSequencial || props.defaultSequencial == 0) {
            novaClasse.sequencial = props.defaultSequencial;
        } else {
            novaClasse.sequencial = props.lista.length + 1;
        }

        let listaLocal = props.lista ? props.lista : [];
        listaLocal.push(novaClasse);
        props.setLista(listaLocal);

        let editingRowsLocal = { ...editingRows, [novaClasse.key]: true };
        setEditingRows(editingRowsLocal);
    }

    const getNextId = () => {
        if (props.lista) {
            return props.lista.length + 1;
        }
        return 1;
    }

    const deleteRow = (rowData: any, index: number) => {
        if (props.lista) {
            deleteEditingRow(rowData.key);

            if (rowData.key == props.selected?.key && props.setSelected) {
                props.setSelected(null);
            }

            props.lista.splice(index, 1);
            props.lista.forEach((item, index) => {
                item.sequencial = index + 1;
            })
            props.onDeleteRow(props.lista);
            props.setLista([...props.lista])

        }

    }

    const onRowEditChange = (e: DataTableRowEditEvent) => {
        setEditingRows(e.data);
    }

    const onEditInit = (event: DataTableRowEditEvent) => {
        let originalRowsLocal = { ...originalRows, [event.data.key]: { ...event.data } };
        setOriginalRows(originalRowsLocal);

        let editingRowsLocal = { ...editingRows, [event.data.key]: true };
        setEditingRows(editingRowsLocal);
    }

    const onEditSave = (event: DataTableRowEditEvent) => {
        deleteOriginalRow(event.data.key);
        deleteEditingRow(event.data.key);
    }

    const onEditCancel = (event: DataTableRowEditEvent) => {
        let listaLocal = [...props.lista];
        if (originalRows[event.data.key]) {
            listaLocal[event.index] = { ...originalRows[event.data.key] };
        } else {
            listaLocal.pop();
        }

        props.setLista(listaLocal);

        deleteOriginalRow(event.data.key);
        deleteEditingRow(event.data.key);
    }

    const deleteOriginalRow = (key: string) => {
        let originalRowsLocal = { ...originalRows };
        delete originalRowsLocal[key];
        setOriginalRows(originalRowsLocal);
    }

    const deleteEditingRow = (key: number) => {
        let editingRowsLocal = { ...editingRows };
        delete editingRowsLocal[key];
        setEditingRows(editingRowsLocal);
    }

    const rowEditorTemplate = (rowData: any, editorProps: any) => {
        const rowEditor = editorProps.rowEditor;
        if (!rowEditor.editing) {
            return rowEditor.element; // default element
        }
        else {

            const rowEditorProps = {
                editing: true,
                saveClassName: 'p-row-editor-save-appso p-link',
                saveIconClassName: 'p-row-editor-save-icon pi pi-fw pi-check',
                cancelClassName: 'p-row-editor-cancel p-link',
                cancelIconClassName: 'p-row-editor-cancel-icon pi pi-fw pi-times'
            };
            return (
                <div style={{ display: "flex", margin: '0 10px', justifyContent: "space-between" }} >
                    <button type="button" name="row-save" onClick={(e) => {
                        editorProps.rowEditor.onSaveClick(e);
                    }} className={rowEditorProps.saveClassName} tabIndex={editorProps.tabIndex}>
                        <span className={rowEditorProps.saveIconClassName}></span>
                        <Ripple />
                    </button>
                    <button type="button" disabled={props.rowCancelDisabled(rowData)} name="row-cancel" onClick={(e) => {
                        editorProps.rowEditor.onCancelClick(e);
                    }} className={rowEditorProps.cancelClassName} tabIndex={editorProps.tabIndex}>
                        <span className={rowEditorProps.cancelIconClassName}></span>
                        <Ripple />
                    </button>
                </div>
            )
        }
    }

    return (

        <DataTable
            emptyMessage="Nenhum registro encontrado."
            value={props.lista}
            header={header}
            dataKey={props.dataKey}
            selection={props.selected!}
            selectionMode={props.isSelection ? "single" : undefined}
            onSelectionChange={(e: any) => {
                if (props.isSelection && props.setSelected) {
                    props.setSelected(e.value);
                }
            }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            editMode="row"
            onRowEditInit={(e) => onEditInit(e)}
            onRowEditSave={(e) => onEditSave(e)}
            onRowEditCancel={(e) => onEditCancel(e)}
            editingRows={editingRows}
            onRowEditChange={(e) => onRowEditChange(e)}
            reorderableRows={props.reordable}
            onRowReorder={props.onRowReorder}
            scrollable={props.scrollable}
            scrollHeight={props.scrollHeight}
            rowGroupMode={props.rowGroupMode}
            groupRowsBy={props.groupRowsBy}
            sortMode={props.sortMode}
            rowGroupHeaderTemplate={props.rowGroupHeaderTemplate}
            rowGroupFooterTemplate={props.rowGroupFooterTemplate}
            headerColumnGroup={props.headerColumnGroup}
        >
            {props.children}
            {props.showEdit ? <Column rowEditor body={(data, options) => rowEditorTemplate(data, options)} bodyStyle={{ textAlign: "center" }} style={{ width: 120 }} /> : null}
            {props.showDelete ? <Column style={{ width: 60 }} body={(rowData, options) => {
                return <Button icon={<FontAwesomeIcon icon="trash" />} onClick={(e) => {
                    deleteRow(rowData, options.rowIndex);
                }} />
            }} /> : null}
        </DataTable>

    )
}

BaseDataTableEdit.defaultProps = {
    isSelection: false,
    dataKey: "id",
    reordable: false,
    onRowReorder: (e: DataTableRowReorderEvent<[] | BaseEditModel[]>) => { },
    addButtonDisabled: false,
    addFunction: () => { },
    scrollable: false,
    scrollHeight: "",
    showEdit: true,
    showDelete: true,
    rowCancelDisabled: (rowData: any) => false,
    onDeleteRow: (list: any[]) => { },

}

export default BaseDataTableEdit;